import { useState, useEffect } from "react";
import { allStages, allAgents } from "@/mocks/leadsData";
import type { LeadStage } from "@/mocks/leadsData";
import { fetchApi } from "@/services/apiService";

type Props = {
  count: number;
  onAssign: (agent: string) => void;
  onChangeStage: (stage: LeadStage) => void;
  onDelete: () => void;
  onClear: () => void;
};

export default function BulkActions({ count, onAssign, onChangeStage, onDelete, onClear }: Props) {
  const [dbStages, setDbStages] = useState<string[]>([]);
  const [dbAgents, setDbAgents] = useState<string[]>([]);

  useEffect(() => {
    fetchApi<any[]>('/config/pipeline-stages/get-all', undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          const names = items.map((s: any) => s.name).filter(Boolean);
          if (names.length > 0) setDbStages(names);
        }
      })
      .catch(() => {});

    fetchApi<any>('/auth/company-user/all', undefined, null)
      .then((res) => {
        const userList = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const agentNames: string[] = [];
        userList.forEach((t: any) => {
          if (t.name) agentNames.push(t.name);
          else if (t.fullName) agentNames.push(t.fullName);
          else if (t.username) agentNames.push(t.username);
          if (t.leader_name) agentNames.push(t.leader_name);
          if (Array.isArray(t.member_names)) agentNames.push(...t.member_names);
        });
        const unique = Array.from(new Set(agentNames)).filter(Boolean);
        if (unique.length > 0) setDbAgents(unique);
      })
      .catch(() => {});
  }, []);

  const stagesList = dbStages.length > 0 ? dbStages : allStages;
  const agentsList = dbAgents.length > 0 ? dbAgents : allAgents;

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 px-4 py-3 bg-indigo-50 border border-indigo-200 rounded-lg">
      <span className="text-sm font-semibold text-indigo-800 whitespace-nowrap">
        {count} lead{count !== 1 ? "s" : ""} selected
      </span>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <select
            onChange={(e) => {
              if (e.target.value) {
                onAssign(e.target.value);
                e.target.value = "";
              }
            }}
            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
          >
            <option value="">Assign To</option>
            {agentsList.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>

        <div className="relative">
          <select
            onChange={(e) => {
              if (e.target.value) {
                onChangeStage(e.target.value as LeadStage);
                e.target.value = "";
              }
            }}
            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
          >
            <option value="">Change Stage</option>
            {stagesList.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={onDelete}
          className="px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-xs font-medium text-red-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-delete-bin-line mr-1" /> Delete
        </button>

        <button
          onClick={onClear}
          className="px-3 py-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          Clear selection
        </button>
      </div>
    </div>
  );
}